import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, Minus, Plus, Sparkle } from "lucide-react";
import { CALC_SERVICES, CALC_FREQUENCY, COMPANY } from "@/data/site";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

export const QuoteCalculator = () => {
  const [serviceId, setServiceId] = useState(CALC_SERVICES[0].id);
  const [qty, setQty] = useState(20);
  const [freqId, setFreqId] = useState(CALC_FREQUENCY[0].id);

  const service = CALC_SERVICES.find((s) => s.id === serviceId);
  const freq = CALC_FREQUENCY.find((f) => f.id === freqId);

  const estimate = useMemo(() => {
    const raw = (service.base + service.perUnit * qty) * freq.factor;
    const low = Math.round((raw * 0.9) / 5) * 5;
    const high = Math.round((raw * 1.15) / 5) * 5;
    return { low, high };
  }, [service, qty, freq]);

  const waLink = useMemo(() => {
    const msg = [
      "Hallo Kilic Gebäudeservice,",
      "",
      `ich habe über den Online-Rechner ein Angebot erstellt:`,
      `• Leistung: ${service.label}`,
      `• Umfang: ${qty} ${service.unit}`,
      `• Intervall: ${freq.label}`,
      `• Geschätzter Preis: ${estimate.low} – ${estimate.high} €`,
      "",
      "Bitte senden Sie mir ein verbindliches Angebot. Danke!",
    ].join("\n");
    return `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(msg)}`;
  }, [service, qty, freq, estimate]);

  const step = (d) => setQty((v) => Math.max(1, Math.min(200, v + d)));

  return (
    <section id="rechner" className="relative overflow-hidden bg-white py-16 md:py-28" data-testid="calculator-section">
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-[var(--kilic-blue)]/10 blur-[120px]" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-[var(--kilic-green)]/10 blur-[120px]" />

      <div className="relative mx-auto max-w-6xl px-5 md:px-8">
        <div className="mx-auto mb-8 max-w-2xl text-center md:mb-14">
          <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-[#fafafa] px-4 py-2 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-[var(--kilic-green-dark)] sm:text-xs sm:tracking-[0.2em]">
            <Calculator className="h-4 w-4" /> Angebots-Rechner
          </span>
          <h2 className="mt-4 font-display text-2xl font-extrabold tracking-tighter text-neutral-900 sm:text-4xl md:text-5xl">
            Preis in <span className="text-gradient">30 Sekunden</span> berechnen
          </h2>
          <p className="mt-3 text-sm text-neutral-600 sm:text-base">
            Stellen Sie Ihre Reinigung zusammen und erhalten Sie sofort eine
            unverbindliche Kostenschätzung – direkt per WhatsApp anfragen.
          </p>
        </div>

        <div className="grid gap-6 rounded-[1.5rem] border border-black/5 bg-[#fafafa] p-4 shadow-sm sm:rounded-[2rem] sm:p-8 lg:grid-cols-5 lg:gap-10">
          {/* Controls */}
          <div className="space-y-6 sm:space-y-8 lg:col-span-3">
            {/* Service select */}
            <div>
              <label className="mb-3 block text-sm font-bold text-neutral-900">
                1. Leistung wählen
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-2.5">
                {CALC_SERVICES.map((s) => {
                  const Icon = s.icon;
                  const active = s.id === serviceId;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setServiceId(s.id)}
                      data-testid={`calc-service-${s.id}`}
                      className={`flex items-center gap-1.5 rounded-xl border p-2.5 text-left text-[0.7rem] font-semibold leading-tight transition-all sm:gap-2 sm:p-3 sm:text-sm ${
                        active
                          ? "border-[var(--kilic-green)] bg-[var(--kilic-green)]/10 text-neutral-900"
                          : "border-black/10 bg-white text-neutral-600 hover:border-neutral-300"
                      }`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${active ? "text-[var(--kilic-green-dark)]" : "text-neutral-400"}`} />
                      <span>{s.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quantity */}
            <div>
              <label className="mb-3 block text-sm font-bold text-neutral-900">
                2. Umfang: <span className="text-[var(--kilic-green-dark)]">{qty} {service.unit}</span>
              </label>
              <div className="flex items-center gap-3 sm:gap-4">
                <button
                  onClick={() => step(-5)}
                  data-testid="calc-qty-minus"
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-neutral-700 shadow-sm ring-1 ring-black/5 transition-colors hover:bg-neutral-50"
                  aria-label="weniger"
                >
                  <Minus className="h-5 w-5" />
                </button>
                <input
                  type="range"
                  min="1"
                  max="200"
                  value={Math.min(qty, 200)}
                  onChange={(e) => setQty(Number(e.target.value))}
                  data-testid="calc-qty-slider"
                  className="h-2 w-full cursor-pointer appearance-none rounded-full bg-neutral-200 accent-[var(--kilic-green)]"
                />
                <button
                  onClick={() => step(5)}
                  data-testid="calc-qty-plus"
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-neutral-700 shadow-sm ring-1 ring-black/5 transition-colors hover:bg-neutral-50"
                  aria-label="mehr"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Frequency */}
            <div>
              <label className="mb-3 block text-sm font-bold text-neutral-900">
                3. Intervall
              </label>
              <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                {CALC_FREQUENCY.map((f) => {
                  const active = f.id === freqId;
                  return (
                    <button
                      key={f.id}
                      onClick={() => setFreqId(f.id)}
                      data-testid={`calc-freq-${f.id}`}
                      className={`rounded-xl border p-2.5 text-xs font-semibold transition-all sm:p-3 sm:text-sm ${
                        active
                          ? "border-[var(--kilic-blue)] bg-[var(--kilic-blue)]/10 text-[var(--kilic-blue)]"
                          : "border-black/10 bg-white text-neutral-600 hover:border-neutral-300"
                      }`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="lg:col-span-2">
            <motion.div
              key={`${estimate.low}-${estimate.high}`}
              initial={{ scale: 0.98, opacity: 0.6 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="flex h-full flex-col justify-between rounded-2xl bg-gradient-to-br from-[var(--kilic-blue)] to-[#0c2f7a] p-6 text-white sm:p-8"
            >
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/70">
                  <Sparkle className="h-4 w-4 text-[var(--kilic-green)]" /> Ihre Schätzung
                </div>
                <div className="mt-4 font-display text-4xl font-extrabold leading-none sm:text-5xl" data-testid="calc-estimate">
                  {estimate.low}–{estimate.high}
                  <span className="ml-1 text-2xl">€</span>
                </div>
                <p className="mt-3 text-sm text-white/70">
                  {service.label} · {qty} {service.unit} · {freq.label}
                </p>
                <p className="mt-4 text-xs text-white/50">
                  Unverbindliche Orientierung. Der finale Preis hängt von den
                  Gegebenheiten vor Ort ab.
                </p>
              </div>

              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calc-whatsapp-btn"
                className="mt-6 flex items-center justify-center gap-3 rounded-xl bg-[var(--kilic-green)] px-5 py-4 font-bold text-white shadow-lg transition-transform hover:scale-[1.03] active:scale-95"
              >
                <WhatsAppIcon className="h-5 w-5" />
                Angebot anfragen
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
